import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { AuthGuard } from '../auth/AuthGuard'
import { NavShell } from './NavShell'
import { AccountsListScreen } from '../screens/accounts/AccountsListScreen'
import { AccountDetailScreen } from '../screens/accounts/AccountDetailScreen'
import { LegalEntitiesListStub } from '../screens/legal-entities/LegalEntitiesListStub'
import { LegalEntityDetailScreen } from '../screens/legal-entities/LegalEntityDetailScreen'
import { NewLegalEntityStub } from '../screens/legal-entities/NewLegalEntityStub'
import { ContactsListScreen } from '../screens/contacts/ContactsListScreen'
import { ContactDetailScreen } from '../screens/contacts/ContactDetailScreen'
import { BranchesListScreen } from '../screens/branches/BranchesListScreen'
import { BranchDetailScreen } from '../screens/branches/BranchDetailScreen'
import { DataImportScreen } from '../screens/data/DataImportScreen'
import { BulkOperationsScreen } from '../screens/bulk/BulkOperationsScreen'
import { AdminSettingsScreen } from '../screens/admin/AdminSettingsScreen'
import { DashboardScreen } from '../screens/insights/DashboardScreen'
import { ReportsScreen } from '../screens/insights/ReportsScreen'
import { JurisdictionAnalyticsContainer } from '../screens/insights/dashboards/jurisdictions/JurisdictionAnalyticsContainer'
import { PortfolioAnalyticsContainer } from '../screens/insights/dashboards/portfolio/PortfolioAnalyticsContainer'
import { OperationsAnalyticsContainer } from '../screens/insights/dashboards/operations/OperationsAnalyticsContainer'
import { DashboardThemeProvider } from '../theme/DashboardThemeProvider'
import { FilingsListScreen } from '../screens/cases/FilingsListScreen'
import { CasesListScreen } from '../screens/cases/CasesListScreen'
import { CaseDetailScreen } from '../screens/cases/CaseDetailScreen'
import { NewCaseScreen } from '../screens/cases/NewCaseScreen'
import { ProductionDetailScreen } from '../screens/productions/ProductionDetailScreen'
import { NewProductionScreen } from '../screens/productions/NewProductionScreen'
import { MyWorkScreen } from '../screens/tasks/MyWorkScreen'
import { NotificationsScreen } from '../screens/notifications/NotificationsScreen'
import { UsersListScreen } from '../screens/admin/users/UsersListScreen'
import { SecurityAdministrationScreen } from '../screens/admin/security/SecurityAdministrationScreen'
import { ProgramsGridScreen } from '../screens/admin/cartridge-studio/ProgramsGridScreen'
import { ProgramWorkspaceScreen } from '../screens/admin/cartridge-studio/ProgramWorkspaceScreen'
import { EligibilityOverlaysListScreen } from '../screens/admin/eligibility-overlays/EligibilityOverlaysListScreen'
import { OverlayAuthorScreen } from '../screens/admin/eligibility-overlays/OverlayAuthorScreen'
import { OverlayDetailScreen } from '../screens/admin/eligibility-overlays/OverlayDetailScreen'

/**
 * The product route table. `/` redirects straight to the Accounts list (the real IA landing page,
 * CLAUDE.md §4 "Clients · Cases · My Work · Insights · Data · Admin") rather than the bare
 * placeholder shell — `App.tsx` itself is unchanged and still rendered directly (no router
 * context) by `App.test.tsx`; only this route's wiring changed. Everything else lives under
 * `NavShell` — the grouped IA.
 *
 * ADR-0048 (Phase A, #7): every route is wrapped in `<AuthGuard>` — an unauthenticated visitor sees
 * ONLY the login screen, never any route's `element` underneath it.
 */
export function AppRouter() {
  return (
    <BrowserRouter>
      <AuthGuard>
        <Routes>
          <Route path="/" element={<Navigate to="/clients/accounts" replace />} />
          <Route element={<NavShell />}>
            <Route path="/clients/accounts" element={<AccountsListScreen />} />
            <Route path="/clients/accounts/:id" element={<AccountDetailScreen />} />
            <Route path="/clients/legal-entities" element={<LegalEntitiesListStub />} />
            <Route path="/clients/legal-entities/new" element={<NewLegalEntityStub />} />
            <Route path="/clients/legal-entities/:id" element={<LegalEntityDetailScreen />} />
            <Route path="/clients/contacts" element={<ContactsListScreen />} />
            <Route path="/clients/contacts/:id" element={<ContactDetailScreen />} />
            <Route path="/clients/branches" element={<BranchesListScreen />} />
            <Route path="/clients/branches/:id" element={<BranchDetailScreen />} />
            <Route path="/cases" element={<CasesListScreen />} />
            <Route path="/cases/filings" element={<FilingsListScreen />} />
            {/* IB-326 — the in-app "New Case" wizard (Film & Media this story); a real, literal
                path ranks ahead of the "/cases/:id" dynamic segment under React Router v6's
                ranked matching, so "new" is never swallowed as a case id. */}
            <Route path="/cases/new" element={<NewCaseScreen />} />
            <Route path="/cases/:id" element={<CaseDetailScreen />} />
            <Route path="/productions/:productionId" element={<ProductionDetailScreen />} />
            {/* IB-327 — the in-app "New Production" wizard. */}
            <Route path="/productions/new" element={<NewProductionScreen />} />
            {/* IB-159 — the Dossier tab lives ON CaseDetailScreen (client-side tab state, ≤2 clicks),
                not a separate screen; this route just lands directly on that tab for a bookmarked/
                deep link (`initialTab`, an ordinary prop on the SAME `element`). */}
            <Route path="/cases/:id/dossier" element={<CaseDetailScreen initialTab="dossier" />} />
            {/* Global Search's Task results deep-link here (a Task's real "case route" — CaseDetailScreen
                has no per-task detail view, TasksPanel is the closest real destination) — same
                initialTab-via-a-dedicated-route pattern as the "/dossier" route just above. */}
            <Route path="/cases/:id/tasks" element={<CaseDetailScreen initialTab="tasks" />} />
            <Route path="/my-work" element={<MyWorkScreen />} />
            <Route path="/my-work/notifications" element={<NotificationsScreen />} />
            {/* IB-292 — DashboardThemeProvider wraps the whole /insights/dashboards* route group
                (one global, per-user dashboard colour theme, Decision 7) via <Outlet/>, additively —
                no existing route element below is modified. */}
            <Route element={<DashboardThemeProvider />}>
              <Route path="/insights/dashboards" element={<DashboardScreen />} />
              <Route path="/insights/dashboards/portfolio" element={<PortfolioAnalyticsContainer />} />
              <Route path="/insights/dashboards/jurisdictions" element={<JurisdictionAnalyticsContainer />} />
              <Route path="/insights/dashboards/operations" element={<OperationsAnalyticsContainer />} />
            </Route>
            <Route path="/insights/reports" element={<ReportsScreen />} />
            <Route path="/data/import" element={<DataImportScreen />} />
            <Route path="/data/bulk" element={<BulkOperationsScreen />} />
            {/* user-admin-entra-federation Slice B — the real User Admin screen (invite/list/
                deactivate/reactivate/assign), capability-gated (user:F et al). Kept alongside the
                product-lineage Admin Settings screen so both capabilities survive the v64 merge. */}
            <Route path="/admin/users" element={<UsersListScreen />} />
            {/* IB-337 (≡ IB-88 ≡ IB-43, docs/design/role-model-reconciliation.md §4) — OLS/FLS/
                UI-component editors + Role/Territory tree console, gated server-side on
                security_config:F. */}
            <Route path="/admin/security" element={<SecurityAdministrationScreen />} />
            <Route path="/admin/settings" element={<AdminSettingsScreen />} />
            {/* IB-210 Slice 2 (demo v66) — Cartridge Authoring Studio, PlatformAdmin-only (design doc
                §1). Read endpoints are open canon (ADR-0017); every mutation is fail-closed guarded
                server-side to the platform tenant — no separate client-side role gate needed. */}
            <Route path="/admin/cartridge-studio" element={<ProgramsGridScreen />} />
            <Route path="/admin/cartridge-studio/programs/:programId" element={<ProgramWorkspaceScreen />} />
            {/* IB-289 (FR-M7.6 UI, IB-211 S6) — tenant eligibility overlay authoring, a DISTINCT
                tenant-facing route OUTSIDE /admin/cartridge-studio/* (design doc §3.1, ADR-0056
                §3.1/R-33): canon appears only as a read-only reference here, never an editable
                shell, so a tenant author is never led to believe they are editing canon. */}
            <Route path="/admin/eligibility-overlays" element={<EligibilityOverlaysListScreen />} />
            <Route path="/admin/eligibility-overlays/new" element={<OverlayAuthorScreen />} />
            <Route path="/admin/eligibility-overlays/:overlayId" element={<OverlayDetailScreen />} />
          </Route>
        </Routes>
      </AuthGuard>
    </BrowserRouter>
  )
}
